import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
// =============================================================================
//  Copyright : 2018 semantics GmbH
//  Date      : $Date: 2025-09-21 19:16:35 +0200 (Sun, 21 Sep 2025) $
//  Author    : Dirk Rothe
//  Worker    : $Author: d.rothe $
//  Revision  : $Rev: 156393 $
//  Purpose   : wrap codemirror
// =============================================================================
// ---Third-party imports
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Menu } from 'semantic-ui-react';
import clsx from 'clsx';
// ---Framework imports
import { isArray, isNil, size } from 'ldash';
import { CodeMirror, DiffView } from 'uix/chunks';
import { hashObj, MIME } from 'uix/utils';
import log from 'uix/logging';
import connect from 'uix/connect';
import { useRef, useEffect, useDimensions, useState, useSelector, usePersistentShallow, useCallback, useMemo, useChunk } from 'uix/hooks';
// ---Local imports
import { default as AutoSizer } from './autosizer/AutoSizer';
import { AutoPortal } from './portals';
import { XMenu } from './menu';
import { Toolbar } from './toolbar';
import S from './editor.scss';
// ---Globals
const GUTTER_ID = 'CustomMarker';
function escapeRegExp(str) {
    return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}
function customHighlightWordsOverlay(words) {
    const escaped = words.map(escapeRegExp);
    // Match only when surrounded by non-[\w$] or string edges
    const regex = new RegExp(`(?:^|[^\\w$])(${escaped.join('|')})(?=$|[^\\w$])`);
    return {
        token: function(stream) {
            if (stream.match(regex)) {
                return 'custom-highlight' // maps to CSS class
                ;
            }
            while(stream.next() != null){
                if (!/[\w$]/.test(stream.peek())) break;
            }
            return null;
        }
    };
}
function staticWordHints(cm, { words, caseSensitive = false } = {}, EditorUtils) {
    const cur = cm.getCursor();
    const token = cm.getTokenAt(cur);
    const word = token.string;
    const startCh = token.start;
    const endCh = token.end;
    // Only trigger on identifiers (adjust regex as you wish)
    const ident = /^[A-Za-z_$][\w$]*$/;
    const inWord = ident.test(word);
    const norm = (s)=>caseSensitive ? s : s.toLowerCase();
    const needle = norm(inWord ? word : '');
    // Unique + filtered list
    const uniq = Array.from(new Set(words));
    const list = uniq.filter((w)=>needle === '' || norm(w).startsWith(needle)).sort((a, b)=>a.localeCompare(b));
    // Define the replacement range
    const from = inWord ? EditorUtils.CodeMirror_Pos(cur.line, startCh) : cur;
    const to = inWord ? EditorUtils.CodeMirror_Pos(cur.line, endCh) : cur;
    return {
        list,
        from,
        to
    };
}
function attachStaticAutocomplete(editor, words, EditorUtils) {
    // Show on Ctrl-Space
    editor.setOption('extraKeys', {
        ...editor.getOption('extraKeys') || {},
        'Ctrl-Space': 'autocomplete',
        'Cmd-Space': 'autocomplete'
    });
    // Preferred hint options
    editor.setOption('hintOptions', {
        hint: (cm)=>staticWordHints(cm, {
                words
            }, EditorUtils),
        completeSingle: false
    });
    // Auto-trigger when typing identifiers
    editor.on('inputRead', (cm, change)=>{
        if (change.text && change.text.length === 1 && /\w/.test(change.text[0])) {
            cm.showHint();
        }
    });
}
const getCodemirrorTheme = (theme)=>theme == 'dark' ? 'lesser-dark' : 'default';
export const mimetype2options = (mimetype)=>{
    let xml_opts = {
        autoCloseTags: true,
        xml: true,
        matchTags: {
            bothTags: true
        }
    };
    if (mimetype == MIME.MARKDOWN) {
        return {
            mode: {
                name: 'markdown',
                // allow lazy headers without whitespace between hashtag and text
                allowAtxHeaderWithoutSpace: true,
                // Whether to separately highlight markdown meta characterts (*[]()etc.)
                highlightFormatting: true
            },
            options: xml_opts,
            extraKeys: {
                Enter: 'newlineAndIndentContinueMarkdownList'
            }
        };
    } else if (mimetype == MIME.HTML) {
        // better folding than markown/html-mixed
        return {
            mode: {
                name: 'text/html'
            },
            options: xml_opts
        };
    } else if (mimetype == MIME.XML || mimetype == MIME.XHTML || mimetype == MIME.XSLT || mimetype == MIME.HOCR || mimetype == MIME.TEI || mimetype?.endsWith('+xml')) {
        return {
            mode: {
                name: 'xml'
            },
            options: xml_opts,
            extraKeys: {
                'Ctrl-J': 'toMatchingTag'
            }
        };
    } else if ([
        MIME.JS,
        MIME.JSON,
        MIME.JSONLD,
        MIME.CSS
    ].includes(mimetype)) {
        // codemirror supports custom rules for json and jsonld mimetypes
        return {
            mode: {
                name: mimetype
            }
        };
    } else {
        log.warn('unknown mimetype: %o', mimetype);
    }
    return {
        mode: {
            name: 'plain'
        }
    };
};
function deriveStateFromProps(props, prevState = {}) {
    const next = {};
    if (prevState.textId !== hashObj(props.text)) {
        const { cursor, scroll } = props;
        if (cursor) next.cursor = cursor;
        if (scroll) next.scroll = scroll;
        Object.assign(next, {
            textId: hashObj(props.text),
            currentText: props.text ?? '',
            changesPending: false
        });
    }
    return next;
}
export const Editor = ({ mimetype = null, text = '', opts = {}, flex = false, bordered = false, // optional props passed through
markers, extraKeys, customHighlightWords, editorDidMount, editorWillUnmount, onBeforeChange, onCursor, onScroll, onContextMenu, ...restProps })=>{
    let EditorUtils = useChunk('EditorUtils');
    // replaces static storeMap
    const theme = useSelector((root)=>getCodemirrorTheme(root.meta.theme.name));
    // refs for CodeMirror instance and live markers
    const cmRef = useRef(null);
    const markersRef = useRef([]);
    // state
    const [state, setState] = useState(()=>({
            ...deriveStateFromProps({
                text,
                cursor: restProps.cursor,
                scroll: restProps.scroll
            }, {}),
            cursor: {
                line: 0,
                ch: 0
            },
            scroll: {
                x: 0,
                y: 0
            }
        }));
    // keep state in sync with incoming props (mimics getDerivedStateFromProps)
    useEffect(()=>{
        setState((prev)=>{
            const patch = deriveStateFromProps({
                text,
                cursor: restProps.cursor,
                scroll: restProps.scroll
            }, prev);
            return Object.keys(patch).length ? {
                ...prev,
                ...patch
            } : prev;
        });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        text,
        restProps.cursor,
        restProps.scroll
    ]);
    const updateMarkText = useCallback(()=>{
        const cm = cmRef.current;
        if (!cm) return;
        const css = 'text-decoration: underline;text-decoration-style: wavy; text-decoration-color:red;';
        // clear old char-level markers
        markersRef.current.forEach((m)=>m.clear?.());
        markersRef.current = [];
        // clear line-level gutter markers
        cm.clearGutter(GUTTER_ID);
        const makeMarker = (sign, msg, color)=>{
            const marker = document.createElement('div');
            marker.setAttribute('title', msg);
            marker.style.color = color;
            marker.style['margin-left'] = '-5px';
            marker.style['width'] = '10px'; // better title-hovering
            marker.style['font-size'] = '1.5em'; // better title-hovering
            marker.innerHTML = sign;
            return marker;
        };
        if (markers && Array.isArray(markers)) {
            for (const m of markers){
                const { line, column, title, ...markerOpts } = m;
                if (column && isArray(column)) {
                    const [left_ch, right_ch] = column;
                    const marker = cm.markText({
                        line: line - 1,
                        ch: left_ch
                    }, {
                        line: line - 1,
                        ch: right_ch
                    }, {
                        css,
                        title,
                        ...markerOpts
                    });
                    markersRef.current.push(marker);
                } else {
                    cm.setGutterMarker(line - 1, GUTTER_ID, makeMarker('&#9888;', title, '#F44'));
                }
            }
        }
    }, [
        markers
    ]);
    // mimic componentDidUpdate for markers/text
    useEffect(()=>{
        if (cmRef.current) updateMarkText();
    }, [
        text,
        markers,
        updateMarkText
    ]);
    const handleMount = useCallback((editor, width, height)=>{
        const { scroll } = state;
        editor.setSize(width, height);
        cmRef.current = editor;
        updateMarkText();
        // after setSize
        editor.scrollTo(scroll.x, scroll.y);
        if (size(customHighlightWords) > 0) {
            if (!EditorUtils) {
                log.error('no editor utils during mount (should not happen)');
            }
            editor.addOverlay(customHighlightWordsOverlay(customHighlightWords));
            attachStaticAutocomplete(editor, customHighlightWords, EditorUtils); // left commented as original
        }
        if (editorDidMount) {
            editorDidMount(editor);
        }
    }, [
        state,
        customHighlightWords,
        editorDidMount,
        updateMarkText,
        EditorUtils
    ]);
    const handleWillUnmount = useCallback(()=>{
        if (editorWillUnmount) {
            editorWillUnmount();
        }
        cmRef.current = null;
    }, [
        editorWillUnmount
    ]);
    const handleBeforeChange = useCallback((editor, data, value)=>{
        const changesPending = text !== value;
        setState((prev)=>({
                ...prev,
                currentText: value,
                changesPending,
                textId: hashObj(value)
            }));
        if (onBeforeChange) {
            onBeforeChange({
                editor,
                data,
                value,
                changesPending
            });
        }
    }, [
        onBeforeChange,
        text
    ]);
    const handleCursor = useCallback((editor, data)=>{
        if (onCursor) onCursor({
            editor,
            data
        });
    }, [
        onCursor
    ]);
    const handleScroll = useCallback((editor, data)=>{
        if (onScroll) onScroll({
            editor,
            data: {
                x: data.left,
                y: data.top
            }
        });
    }, [
        onScroll
    ]);
    const handleSelection = useCallback(()=>{
    // no-op, mirrors original
    }, []);
    const handleContextMenu = useCallback((editor, event)=>{
        if (!event.ctrlKey && onContextMenu) {
            onContextMenu({
                editor,
                event
            });
        }
    }, [
        onContextMenu
    ]);
    const { mode, options: mimeOptions = {}, extraKeys: mimetypeExtraKeys = {} } = useMemo(()=>mimetype ? mimetype2options(mimetype) : {
            mode: undefined,
            options: {},
            extraKeys: {}
        }, [
        mimetype
    ]);
    const mergedExtraKeys = useMemo(()=>({
            'Ctrl-Q': (cm)=>cm.foldCode(cm.getCursor()),
            F11: (cm)=>cm.setOption('fullScreen', !cm.getOption('fullScreen')),
            Esc: (cm)=>cm.getOption('fullScreen') ? cm.setOption('fullScreen', false) : null,
            // https://github.com/codemirror/CodeMirror/issues/988#issuecomment-392232020
            Tab: (cm)=>cm.execCommand('indentMore'),
            'Shift-Tab': (cm)=>cm.execCommand('indentLess'),
            ...mimetypeExtraKeys,
            ...extraKeys
        }), [
        mimetypeExtraKeys,
        extraKeys
    ]);
    const renderEditor = useCallback(({ width, height })=>{
        if (bordered) {
            width -= 2;
            height -= 2;
        }
        if (cmRef.current && !cmRef.current.getOption('fullScreen')) {
            cmRef.current.setSize(width, height);
        }
        const options = {
            mode,
            theme,
            lineNumbers: true,
            lineWrapping: false,
            foldGutter: true,
            gutters: [
                'CodeMirror-linenumbers',
                'CodeMirror-foldgutter',
                GUTTER_ID
            ],
            keyMap: 'sublime',
            extraKeys: mergedExtraKeys,
            autoFocus: false,
            readOnly: false,
            ...mimeOptions,
            ...opts
        };
        return /*#__PURE__*/ _jsx(CodeMirror, {
            value: state.currentText,
            cursor: state.cursor,
            autoScroll: false,
            editorDidMount: (editor)=>handleMount(editor, width, height),
            editorWillUnmount: handleWillUnmount,
            options: options,
            selection: {
                ranges: [],
                focus: false
            },
            onBeforeChange: handleBeforeChange,
            onChange: ()=>null,
            onCursor: handleCursor,
            onScroll: handleScroll,
            onSelection: handleSelection,
            onContextMenu: handleContextMenu,
            ...restProps
        });
    }, [
        bordered,
        handleBeforeChange,
        handleContextMenu,
        handleCursor,
        handleMount,
        handleScroll,
        handleSelection,
        handleWillUnmount,
        mergedExtraKeys,
        mimeOptions,
        mode,
        opts,
        state.currentText,
        state.cursor,
        theme,
        restProps
    ]);
    if (!EditorUtils) {
        return null;
    }
    return /*#__PURE__*/ _jsx("div", {
        className: clsx(flex && S.flex, bordered && S.bordered),
        children: /*#__PURE__*/ _jsx(AutoSizer, {
            defaultWidth: 500,
            defaultHeight: 300,
            children: renderEditor
        })
    });
};
export const SyntaxHighlighter = ({ text, bordered, opts, flex, disableHeight = false, mimetype = null, style })=>{
    let options = {
        readOnly: true,
        lineNumbers: false,
        lineWrapping: true,
        foldGutter: true,
        gutters: [
            'CodeMirror-foldgutter'
        ]
    };
    let { mode = {
        name: 'xml'
    }, options: mimeOptions = {} } = mimetype ? mimetype2options(mimetype) : {};
    let [dimensions, ref] = useDimensions({
        delta: 50
    });
    options = {
        mode,
        ...options,
        ...opts,
        ...mimeOptions
    };
    let cmRef = useRef(null);
    let mountEditor = (editor)=>cmRef.current = editor;
    useEffect(()=>{
        let { width, height } = dimensions;
        if (bordered) {
            width -= 2;
            height -= 2;
        }
        if (cmRef.current) {
            cmRef.current.setSize(width, height);
        }
    }, [
        dimensions.width,
        dimensions.height,
        bordered
    ]);
    return /*#__PURE__*/ _jsx("div", {
        ref: ref,
        style: style,
        className: clsx(flex && S.flex, bordered && S.bordered),
        children: /*#__PURE__*/ _jsx(CodeMirror, {
            value: text,
            options: options,
            editorDidMount: mountEditor
        })
    });
};
const INITIAL_DIFFER_OPTIONS = {
    lineWrapping: true,
    connect: null,
    highlightDifferences: true,
    ignoreWhitespace: false,
    collapseIdentical: false
};
export const Differ = ({ mode, textLeft, textMiddle, flex, collapseIdentical, menuPortal, bareMenuItems = false, lineNumbers, lineWrapping, onChange, headLeft, headMiddle, footerLeft, footerMiddle })=>{
    let theme = useSelector((root)=>getCodemirrorTheme(root.meta.theme.name));
    let [diffopts, setOpts] = usePersistentShallow(`differ`, INITIAL_DIFFER_OPTIONS, 1);
    let doOnChange = (editor, data, value)=>{
        if (onChange) {
            onChange({
                editor,
                data,
                value
            });
        }
    };
    //~ let [orgTextMiddle, setOrgTextMiddle] = useState(null)
    //~ useEffect(() => {
    //~ if (!orgTextMiddle) {
    //~ setOrgTextMiddle(textMiddle)
    //~ }
    //~ }, [textMiddle])
    let orgTextMiddle = textMiddle;
    let [editor, setEditor] = useState(null);
    let [leftEditor, setLeftEditor] = useState(null);
    let onEditorMount = (editor, leftEditor)=>{
        //~ log.x('mount', editor)
        //~ setx(33)
        //~ log.x(editor)
        setEditor(editor);
        setLeftEditor(leftEditor);
    };
    let buildCaptions = (left, middle)=>{
        if (left || middle) {
            return /*#__PURE__*/ _jsxs("div", {
                style: {
                    display: 'flex',
                    backgroundColor: '#ddd'
                },
                children: [
                    /*#__PURE__*/ _jsx("div", {
                        style: {
                            flex: 0.5,
                            paddingLeft: '0.1em'
                        },
                        children: left
                    }),
                    /*#__PURE__*/ _jsx("div", {
                        style: {
                            flex: 0.5,
                            paddingLeft: '2.1em'
                        },
                        children: middle
                    })
                ]
            });
        }
        return null;
    };
    // hardlocked mode="sui"
    // use-case: DeltaReasonPortal (Target) and bareMenuItems
    let menu = /*#__PURE__*/ _jsx(_Fragment, {
        children: /*#__PURE__*/ _jsxs(Toolbar.Group, {
            separator: true,
            position: "right",
            title: "Settings",
            children: [
                /*#__PURE__*/ _jsx(XMenu.Item, {
                    mode: "sui",
                    active: diffopts.lineWrapping,
                    onClick: ()=>setOpts({
                            lineWrapping: !diffopts.lineWrapping
                        }),
                    icon: "linewrap",
                    title: "Textwrap"
                }),
                /*#__PURE__*/ _jsx(XMenu.Item, {
                    mode: "sui",
                    active: diffopts.connect === 'align',
                    onClick: ()=>setOpts({
                            connect: !diffopts.connect ? 'align' : null
                        }),
                    icon: "linkify",
                    title: "Connect"
                }),
                /*#__PURE__*/ _jsx(XMenu.Item, {
                    mode: "sui",
                    active: diffopts.highlightDifferences,
                    onClick: ()=>setOpts({
                            highlightDifferences: !diffopts.highlightDifferences
                        }),
                    icon: "lightbulb outline",
                    title: "Highlight"
                }),
                /*#__PURE__*/ _jsx(XMenu.Item, {
                    mode: "sui",
                    active: diffopts.ignoreWhitespace,
                    onClick: ()=>setOpts({
                            ignoreWhitespace: !diffopts.ignoreWhitespace
                        }),
                    icon: "ellipsis horizontal",
                    title: "IgnoreWhitespace"
                }),
                /*#__PURE__*/ _jsx(XMenu.Item, {
                    mode: "sui",
                    onClick: ()=>editor.execCommand('goPrevDiff'),
                    icon: "arrow left",
                    title: "Prev"
                }),
                /*#__PURE__*/ _jsx(XMenu.Item, {
                    mode: "sui",
                    onClick: ()=>editor.execCommand('goNextDiff'),
                    icon: "arrow right",
                    title: "Next"
                }),
                collapseIdentical === undefined && /*#__PURE__*/ _jsx(XMenu.Toggle, {
                    mode: "sui",
                    value: diffopts.collapseIdentical,
                    set: (value)=>setOpts({
                            collapseIdentical: value
                        }),
                    icon: "compress",
                    title: "Collapse Identical"
                })
            ]
        })
    });
    // if (!bareMenuItems) {
    //   menu = <Toolbar>{menu}</Toolbar>
    // }
    let readOnly = !Boolean(onChange);
    let cm = ({ width, height })=>{
        if (height == 0) return null;
        // XXX: hack - subtract header/footer height
        let delta_height = headLeft ? 20 : 0;
        delta_height += footerLeft ? 20 : 0;
        return /*#__PURE__*/ _jsx(DiffView, {
            width: width,
            height: height - delta_height,
            options: {
                value: orgTextMiddle || '',
                origLeft: textLeft || '',
                lineNumbers,
                lineWrapping: isNil(lineWrapping) ? diffopts.lineWrapping : lineWrapping,
                highlightDifferences: diffopts.highlightDifferences,
                ignoreWhitespace: diffopts.ignoreWhitespace,
                connect: diffopts.connect,
                mode,
                theme,
                collapseIdentical: collapseIdentical ?? diffopts.collapseIdentical,
                readOnly,
                revertButtons: !readOnly
            },
            editorDidMount: onEditorMount,
            onChange: doOnChange
        });
    };
    let result = /*#__PURE__*/ _jsx(AutoSizer, {
        children: cm
    });
    if (flex) {
        result = /*#__PURE__*/ _jsx("div", {
            style: {
                display: 'flex',
                flex: 1
            },
            children: result
        });
    }
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            buildCaptions(headLeft, headMiddle),
            result,
            buildCaptions(footerLeft, footerMiddle),
            menuPortal && /*#__PURE__*/ _jsx(AutoPortal, {
                target: menuPortal,
                children: menu
            })
        ]
    });
};
